import { useEffect, useState, useCallback } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { motion } from "framer-motion";
import reviewImg from "../assets/reviewimg.svg";
import reviewImg1 from "../assets/reviewimg1.svg";
import reviewImg2 from "../assets/reviewimg2.svg";
import reviewImg3 from "../assets/reviewimg3.svg";
import reviewImg4 from "../assets/reviewimg4.svg";

export const reviews = [
  {
    name: "Danielle R.",
    role: "Mandeville, Jamaica",
    img: reviewImg,
    rating: 5,
    text: "The best dental experience I've ever had! The team is professional, kind and my results are amazing.",
  },
  {
    name: "Michael Johnson",
    role: "Manchester, Jamaica",
    img: reviewImg1,
    rating: 4.5,
    text: "Professional staff, modern facilities, and outstanding results. I highly recommend their services.",
  },
  {
    name: "David Brown",
    role: "Mandeville, Jamaica",
    img: reviewImg4,
    rating: 4,
    text: "The treatment was completely stress-free, and the results exceeded my expectations.",
  },
  {
    name: "Daniel Smith",
    role: "Manchester, Jamaica",
    img: reviewImg3,
    rating: 4.5,
    text: "Exceptional service and amazing results. I finally have the confidence to smile again.",
  },
  {
    name: "Sophia Campbell",
    role: "Mandeville, Jamaica",
    img: reviewImg2,
    rating: 4,
    text: "From consultation to treatment, everything was handled professionally and with great attention to detail.",
  },
  {
    name: "Tamara B.",
    role: "Manchester - Cleaning & whitening",
    img: "https://images.pexels.com/photos/3845549/pexels-photo-3845549.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=600",
    rating: 5,
    text: "Felt more like a coastal spa than a dentist. My cleaning and whitening result was beyond what I imagined - and totally painless.",
  },
  {
    name: "Marcus R.",
    role: "Mandeville - Crown & bridge",
    img: "https://images.pexels.com/photos/8498412/pexels-photo-8498412.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=600",
    rating: 5,
    text: "Dr. Baro walked me through every step of my crown and bridge treatment. I smile bigger than I ever used to - honestly life changing.",
  },
  {
    name: "Keisha M.",
    role: "Manchester - Family patient",
    img: "https://images.pexels.com/photos/3845625/pexels-photo-3845625.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=600",
    rating: 5,
    text: "My whole family trusts Be Dental Paradise - from my six-year-old to my grandparents. Calm, patient and beautifully run.",
  },
  {
    name: "Andre P.",
    role: "Mandeville - Whitening",
    img: "https://images.pexels.com/photos/35037064/pexels-photo-35037064.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=600",
    rating: 5,
    text: "Saw real results from the first visit. The team is warm, professional and the clinic is spotless. I recommend them to everyone.",
  },
  {
    name: "Sasha N.",
    role: "Mandeville - prosthodontist rehab",
    img: "https://images.pexels.com/photos/3845657/pexels-photo-3845657.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=600",
    rating: 5,
    text: "After 20 years of dental anxiety, Dr. Baro completely changed my outlook. She really treats you like a person, not a chart.",
  },
];

function RatingStars({ rating = 5, className = "h-4 w-4" }) {
  return (
    <>
      {[...Array(5)].map((_, idx) => {
        const fill = Math.max(0, Math.min(1, rating - idx));

        return (
          <span key={idx} className="relative inline-flex">
            <Star className={`${className} fill-current text-slate-200`} />
            {fill > 0 ? (
              <span className="absolute inset-0 overflow-hidden" style={{ width: `${fill * 100}%` }}>
                <Star className={`${className} fill-current text-paradise-yellow`} />
              </span>
            ) : null}
          </span>
        );
      })}
    </>
  );
}

export default function TestimonialsCarousel({ heading = true }) {
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: "start" }, []);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);
  const scrollTo = useCallback((i) => emblaApi && emblaApi.scrollTo(i), [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setSelectedIndex(emblaApi.selectedScrollSnap());
    emblaApi.on("select", onSelect);
    onSelect();
    const id = setInterval(() => emblaApi.scrollNext(), 5500);
    return () => {
      clearInterval(id);
      emblaApi.off("select", onSelect);
    };
  }, [emblaApi]);

  return (
    <section id="testimonials" className="relative overflow-hidden bg-white py-24 lg:py-32" data-testid="testimonials-section">
      <div className="pointer-events-none absolute -top-32 right-0 h-[400px] w-[400px] rounded-full bg-paradise-yellow/15 blur-3xl" />
      <div className="container-x relative">
        {heading && (
          <div className="mb-12 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.7 }}
              className="max-w-2xl"
            >
              <div className="eyebrow mb-5">Kind words</div>
              <h2 className="font-display text-4xl font-semibold text-paradise-navy sm:text-5xl">
                Real patients. <em className="font-medium not-italic text-paradise-blue">Real confidence</em>.
              </h2>
            </motion.div>
            <div className="flex items-center gap-3">
              <button
                onClick={scrollPrev}
                aria-label="Previous"
                className="flex h-12 w-12 items-center justify-center rounded-full bg-paradise-mist text-paradise-navy transition-colors hover:bg-paradise-blue hover:text-white"
                data-testid="testimonials-prev"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                onClick={scrollNext}
                aria-label="Next"
                className="shadow-vibrant flex h-12 w-12 items-center justify-center rounded-full bg-paradise-blue text-white transition-colors hover:bg-paradise-blueDark"
                data-testid="testimonials-next"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}

        <div className="-mx-4 overflow-hidden px-4" ref={emblaRef} data-testid="testimonials-carousel">
          <div className="flex gap-6">
            {reviews.map((r, i) => (
              <article
  key={`${r.name}-${i}`}
  className="relative flex-[0_0_100%] rounded-3xl border border-paradise-cyan/20 bg-paradise-cyanSoft p-8 transition-all duration-300 hover:-translate-y-1 hover:border-paradise-blue/40 sm:flex-[0_0_calc(50%-12px)] lg:flex-[0_0_calc(33.333%-16px)]"
  data-testid={`testimonial-${i}`}
>
  <Quote className="h-8 w-8 text-paradise-blue/20 mb-3" />
  <p className="font-display text-lg italic leading-snug text-paradise-ink mb-4">{r.text}</p>
  <div className="mb-3 flex items-center gap-1">
    <RatingStars rating={r.rating} />
  </div>
  <div className="flex items-center justify-between gap-4">
    <div>
      <div className="font-semibold text-paradise-navy">-{r.name}</div>
      <div className="text-xs text-paradise-slate">{r.role}</div>
    </div>
    <img src={r.img} alt={r.name} className="shadow-soft h-14 w-14 rounded-full object-cover object-top ring-4 ring-white" />
  </div>
</article>
              // <article
              //   key={`${r.name}-${i}`}
              //   className="relative flex-[0_0_100%] rounded-3xl border border-paradise-cyan/20 bg-paradise-cyanSoft p-8 transition-all duration-300 hover:-translate-y-1 hover:border-paradise-blue/40 sm:flex-[0_0_calc(50%-12px)] lg:flex-[0_0_calc(33.333%-16px)]"
              //   data-testid={`testimonial-${i}`}
              // >
              //   <Quote className="absolute -top-3 left-4 h-12 w-12 text-paradise-blue/15" />
              //   <div className="relative mb-4 flex items-center gap-1">
              //     <RatingStars rating={r.rating} />
              //   </div>
              //   <p className="font-display relative text-xl italic leading-snug text-paradise-ink">"{r.text}"</p>
              //   <div className="mt-8 flex items-center gap-4">
              //     <img src={r.img} alt={r.name} className="shadow-soft h-14 w-14 rounded-full object-cover object-top ring-4 ring-white" />
              //     <div>
              //       <div className="font-semibold text-paradise-navy">{r.name}</div>
              //       <div className="text-xs text-paradise-slate">{r.role}</div>
              //     </div>
              //   </div>
              // </article>
            ))}
          </div>
        </div>

        <div className="mt-10 flex justify-center gap-2">
          {reviews.map((_, i) => (
            <button
              key={i}
              onClick={() => scrollTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              className={`h-2 rounded-full transition-all ${
                selectedIndex === i ? "w-10 bg-paradise-blue" : "w-2 bg-paradise-blue/25"
              }`}
              data-testid={`testimonial-dot-${i}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
